import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Palette, MapPin, Bed, Car, Loader2, Plus, Trash2, Users, Church,
  Route as RouteIcon, TentTree, Home, CheckCircle2, X, User as UserIcon, QrCode, Pencil,
  History, Tv, ShieldCheck, DollarSign, UserPlus, KeyRound, ScanLine, Printer, Sparkles,
  AlertTriangle, Copy, Search, Eye, Download,
} from "lucide-react";

const STATUS_ORDER = ["not_checked_in", "at_church", "left_church", "at_camp", "left_camp", "back_at_church"];
const STATUS_LABEL = {
  not_checked_in: "Sin registrar",
  at_church: "En iglesia",
  left_church: "Salió de la iglesia",
  at_camp: "Llegó al campamento",
  left_camp: "Salió del campamento",
  back_at_church: "De regreso en iglesia",
};
const STATUS_SHORT = {
  not_checked_in: "Sin registrar",
  at_church: "En iglesia",
  left_church: "Salió iglesia",
  at_camp: "En campamento",
  left_camp: "Salió campamento",
  back_at_church: "Regresó a iglesia",
};
const STATUS_COLOR = {
  not_checked_in: "bg-gray-200 text-gray-700 border-gray-300",
  at_church: "bg-blue-100 text-blue-800 border-blue-300",
  left_church: "bg-cyan-100 text-cyan-800 border-cyan-300",
  at_camp: "bg-green-100 text-green-800 border-green-300",
  left_camp: "bg-orange-100 text-orange-800 border-orange-300",
  back_at_church: "bg-purple-100 text-purple-800 border-purple-300",
};
const STATUS_INDEX = STATUS_ORDER.reduce((a, s, i) => { a[s] = i; return a; }, {});
const STATUS_ICON = {
  not_checked_in: UserIcon,
  at_church: Church,
  left_church: RouteIcon,
  at_camp: TentTree,
  left_camp: RouteIcon,
  back_at_church: Home,
};

const COLOR_PRESETS = [
  "#facc15", "#f97316", "#ef4444", "#ec4899", "#8b5cf6",
  "#6366f1", "#3b82f6", "#06b6d4", "#10b981", "#84cc16",
  "#a3a3a3", "#78716c",
];

export default function AdminLogistics() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "status");
  const [regs, setRegs] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [cabins, setCabins] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualOpen, setManualOpen] = useState(false);
  const [me, setMe] = useState(null);

  const role = me?.role || "admin";
  const isAdmin = role === "admin";
  const isKiosko = role === "kiosko";
  const isLogistica = role === "logistica";

  // Which tabs are visible for the current role. Admin sees all.
  const availableTabs = useMemo(() => {
    if (isAdmin) return ["status", "debts", "rooms", "cabins", "vehicles", "colors", "cleanup", "audit", "users"];
    if (isKiosko) return ["status", "debts", "audit"];
    if (isLogistica) return ["rooms", "cabins", "vehicles", "colors", "cleanup"];
    return ["status"];
  }, [isAdmin, isKiosko, isLogistica]);

  const loadAll = async () => {
    try {
      const [r, ro, ca, v, s, m] = await Promise.all([
        api.get("/admin/registrations"),
        api.get("/admin/rooms"),
        api.get("/admin/cabins"),
        api.get("/admin/vehicles"),
        api.get("/admin/logistics/summary"),
        api.get("/admin/me"),
      ]);
      setRegs(r.data);
      setRooms(ro.data);
      setCabins(ca.data);
      setVehicles(v.data);
      setSummary(s.data);
      setMe(m.data);
    } catch (e) {
      if (e?.response?.status === 401) nav("/admin/login");
      else toast.error("Error cargando datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { nav("/admin/login"); return; }
    loadAll();
    const t = setInterval(loadAll, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When role loads, jump to the first tab the user is allowed to see.
  useEffect(() => {
    if (me && !availableTabs.includes(tab)) {
      setTab(availableTabs[0]);
    }
  }, [me, availableTabs, tab]);

  const regById = useMemo(() => {
    const m = {};
    regs.forEach((r) => { m[r.id] = r; });
    return m;
  }, [regs]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {isAdmin ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => nav("/admin")}
                data-testid="logistics-back-btn"
                className="text-white/80 hover:text-yellow-400 hover:bg-white/5 flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Admin
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { localStorage.removeItem("admin_token"); nav(isKiosko ? "/kiosko" : "/logistica"); }}
                data-testid="role-logout-btn"
                className="text-white/80 hover:text-red-400 hover:bg-white/5 flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Salir
              </Button>
            )}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-400/70">
                {isKiosko ? "Kiosco" : isLogistica ? "Logística" : "Panel"}
              </div>
              <h1 className="font-heading uppercase text-xl sm:text-2xl tracking-wide truncate">
                {isKiosko ? "Check-in del campamento" : isLogistica ? "Cabañas y Transporte" : "Logística del campamento"}
              </h1>
            </div>
          </div>
          {summary && (
            <div className="hidden md:flex items-center gap-2 text-xs">
              <span className="px-3 py-1 rounded-full bg-yellow-400/10 border border-yellow-400/30 text-yellow-300 font-bold">
                {summary.total} inscritos
              </span>
            </div>
          )}
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setManualOpen(true)}
              data-testid="manual-add-btn"
              className="text-white/80 hover:text-yellow-400 hover:bg-white/5"
            >
              <UserPlus className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Nuevo</span>
            </Button>
          )}
          {(isAdmin || isKiosko) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nav("/kiosko/scan")}
              data-testid="qr-scan-btn"
              className="bg-yellow-400/10 border border-yellow-400/40 text-yellow-300 hover:bg-yellow-400 hover:text-black font-bold"
            >
              <ScanLine className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Escanear QR</span>
              <span className="sm:hidden">Escanear</span>
            </Button>
          )}
          {(isAdmin || isLogistica) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nav("/admin/logistica/hojas")}
              data-testid="room-sheets-btn"
              className="text-white/80 hover:text-pink-300 hover:bg-white/5"
            >
              <Printer className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Hojas</span>
            </Button>
          )}
          {(isAdmin || isKiosko) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nav("/admin/logistica/tv")}
              data-testid="tv-mode-btn"
              className="text-white/80 hover:text-yellow-400 hover:bg-white/5"
            >
              <Tv className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">TV</span>
            </Button>
          )}
          {(isAdmin || isKiosko) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nav("/admin/logistica/qr")}
              data-testid="qr-sheet-btn"
              className="text-white/80 hover:text-yellow-400 hover:bg-white/5"
            >
              <QrCode className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">QR</span>
            </Button>
          )}
        </div>

        {/* Live status pills */}
        {summary && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-4 flex gap-2 overflow-x-auto scrollbar-none">
            {STATUS_ORDER.map((s) => {
              const Icon = STATUS_ICON[s];
              const count = summary.status_counts?.[s] || 0;
              return (
                <div
                  key={s}
                  data-testid={`status-count-${s}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${STATUS_COLOR[s]} flex-shrink-0`}
                >
                  <Icon className="w-4 h-4" />
                  <div className="text-xs leading-tight">
                    <div className="font-bold">{count}</div>
                    <div className="opacity-70 text-[10px]">{STATUS_SHORT[s]}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            className="bg-white/5 border border-white/10 flex flex-wrap gap-1 w-full mb-6 h-auto p-1"
          >
            {availableTabs.includes("status") && (
              <TabsTrigger value="status" data-testid="tab-status" className="flex-1 min-w-[110px] data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
                <MapPin className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Check-in</span>
              </TabsTrigger>
            )}
            {availableTabs.includes("debts") && (
              <TabsTrigger value="debts" data-testid="tab-debts" className="flex-1 min-w-[110px] data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
                <DollarSign className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Deudas</span>
              </TabsTrigger>
            )}
            {availableTabs.includes("rooms") && (
              <TabsTrigger value="rooms" data-testid="tab-rooms" className="flex-1 min-w-[110px] data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
                <Bed className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Habitaciones</span>
              </TabsTrigger>
            )}
            {availableTabs.includes("cabins") && (
              <TabsTrigger value="cabins" data-testid="tab-cabins" className="flex-1 min-w-[110px] data-[state=active]:bg-pink-400 data-[state=active]:text-black">
                <Sparkles className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Cabañas VIP</span>
              </TabsTrigger>
            )}
            {availableTabs.includes("vehicles") && (
              <TabsTrigger value="vehicles" data-testid="tab-vehicles" className="flex-1 min-w-[110px] data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
                <Car className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Transporte</span>
              </TabsTrigger>
            )}
            {availableTabs.includes("colors") && (
              <TabsTrigger value="colors" data-testid="tab-colors" className="flex-1 min-w-[110px] data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
                <Palette className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Colores</span>
              </TabsTrigger>
            )}
            {availableTabs.includes("cleanup") && (
              <TabsTrigger value="cleanup" data-testid="tab-cleanup" className="flex-1 min-w-[110px] data-[state=active]:bg-orange-400 data-[state=active]:text-black">
                <AlertTriangle className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Sin asignar</span>
              </TabsTrigger>
            )}
            {availableTabs.includes("audit") && (
              <TabsTrigger value="audit" data-testid="tab-audit" className="flex-1 min-w-[110px] data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
                <History className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Auditoría</span>
              </TabsTrigger>
            )}
            {availableTabs.includes("users") && (
              <TabsTrigger value="users" data-testid="tab-users" className="flex-1 min-w-[110px] data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
                <KeyRound className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Usuarios</span>
              </TabsTrigger>
            )}
          </TabsList>

          {availableTabs.includes("status") && (
            <TabsContent value="status">
              <CheckInTab regs={regs} onChange={loadAll} isAdmin={isAdmin} />
            </TabsContent>
          )}
          {availableTabs.includes("debts") && (
            <TabsContent value="debts">
              <DebtsTab regs={regs} onChange={loadAll} />
            </TabsContent>
          )}
          {availableTabs.includes("rooms") && (
            <TabsContent value="rooms">
              <RoomsTab rooms={rooms} regs={regs} regById={regById} onChange={loadAll} />
            </TabsContent>
          )}
          {availableTabs.includes("cabins") && (
            <TabsContent value="cabins">
              <CabinsTab cabins={cabins} regs={regs} regById={regById} onChange={loadAll} />
            </TabsContent>
          )}
          {availableTabs.includes("vehicles") && (
            <TabsContent value="vehicles">
              <VehiclesTab vehicles={vehicles} regs={regs} regById={regById} onChange={loadAll} />
            </TabsContent>
          )}
          {availableTabs.includes("colors") && (
            <TabsContent value="colors">
              <ColorsTab regs={regs} onChange={loadAll} />
            </TabsContent>
          )}
          {availableTabs.includes("cleanup") && (
            <TabsContent value="cleanup">
              <CleanupTab rooms={rooms} cabins={cabins} regs={regs} onChange={loadAll} isAdmin={isAdmin} />
            </TabsContent>
          )}
          {availableTabs.includes("audit") && (
            <TabsContent value="audit">
              <AuditTab />
            </TabsContent>
          )}
          {availableTabs.includes("users") && (
            <TabsContent value="users">
              <UsersTab />
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Manual add modal */}
      <ManualAddModal open={manualOpen} onClose={() => setManualOpen(false)} onCreated={loadAll} />
    </div>
  );
}

// ==================== CHECK-IN TAB ====================
function CheckInTab({ regs, onChange, isAdmin }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [saving, setSaving] = useState(null);

  const filtered = regs.filter((r) => {
    if (filter !== "all" && (r.status_location || "not_checked_in") !== filter) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.full_name || "").toLowerCase().includes(s) ||
      (r.phone || "").includes(s) ||
      (r.camper_number_display || "").includes(s);
  });

  const updateStatus = async (reg, status, isBackward = false) => {
    if (isBackward) {
      if (!isAdmin) { toast.error("Solo el administrador puede revertir un estado"); return; }
      if (!window.confirm(`¿Revertir el estado de ${reg.full_name} a "${STATUS_LABEL[status]}"? Solo hazlo si es una corrección de error.`)) return;
    }
    setSaving(reg.id);
    try {
      await api.patch(`/admin/registrations/${reg.id}/status`, { status_location: status, force: isBackward });
      toast.success(`${reg.full_name}: ${STATUS_SHORT[status]}${isBackward ? " (revertido)" : ""}`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setSaving(null);
    }
  };

  const revertOneStep = (reg) => {
    const currentIdx = STATUS_INDEX[reg.status_location || "not_checked_in"] ?? 0;
    if (currentIdx <= 0) { toast.error("Ya está en el estado inicial"); return; }
    const previous = STATUS_ORDER[currentIdx - 1];
    updateStatus(reg, previous, true);
  };

  return (
    <div className="space-y-4">
      {/* Big CHECK-IN scan CTA */}
      <button
        onClick={() => window.location.assign("/kiosko/scan")}
        data-testid="checkin-scan-cta"
        className="w-full group relative overflow-hidden rounded-2xl border-2 border-yellow-400 bg-gradient-to-br from-yellow-400 to-yellow-500 text-black py-6 px-6 font-heading uppercase tracking-widest text-2xl sm:text-3xl shadow-lg hover:from-yellow-300 hover:to-yellow-400 transition"
      >
        <div className="flex items-center justify-center gap-3">
          <ScanLine className="w-8 h-8 sm:w-10 sm:h-10" />
          <span>Escanear QR de Check-in</span>
        </div>
        <div className="text-xs sm:text-sm font-sans normal-case tracking-normal mt-1 text-black/70">
          Toca aquí para abrir la cámara y escanear el brazalete
        </div>
      </button>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          data-testid="checkin-search"
          placeholder="Buscar por nombre, #campista o teléfono…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="bg-white/5 border-white/10 !text-white placeholder:text-white/40 sm:max-w-xs"
        />
        <div className="flex flex-wrap gap-1">
          {["all", ...STATUS_ORDER].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`checkin-filter-${f}`}
              className={`px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider whitespace-nowrap border transition-colors ${
                filter === f ? "bg-yellow-400 text-black border-yellow-400" : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
              }`}
            >
              {f === "all" ? "Todos" : STATUS_SHORT[f]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-2">
        {filtered.map((r) => {
          const status = r.status_location || "not_checked_in";
          return (
            <div
              key={r.id}
              data-testid={`checkin-row-${r.id}`}
              className="bg-white/5 border border-white/10 rounded-xl p-3 sm:p-4"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-3 h-10 rounded-full flex-shrink-0"
                  style={{ background: r.color || "#525252" }}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white truncate">{r.full_name}</div>
                  <div className="text-xs text-white/50 flex items-center gap-2">
                    <span className="text-yellow-400 font-mono">{r.camper_number_display}</span>
                    <span>·</span>
                    <span>{r.phone}</span>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-[10px] uppercase font-bold tracking-widest border ${STATUS_COLOR[status]}`}>
                  {STATUS_SHORT[status]}
                </span>
                {isAdmin && STATUS_INDEX[status] > 0 && (
                  <button
                    onClick={() => revertOneStep(r)}
                    data-testid={`checkin-revert-${r.id}`}
                    className="ml-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest bg-red-500/10 text-red-300 border border-red-500/40 hover:bg-red-500 hover:text-white flex items-center gap-1"
                    title="Revertir al estado anterior (solo admin)"
                  >
                    <ArrowLeft className="w-3 h-3" /> Revertir
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                {STATUS_ORDER.map((s) => {
                  const Icon = STATUS_ICON[s];
                  const active = status === s;
                  const isBackward = STATUS_INDEX[s] < STATUS_INDEX[status];
                  return (
                    <button
                      key={s}
                      onClick={() => updateStatus(r, s, isBackward)}
                      disabled={saving === r.id || active}
                      data-testid={`checkin-btn-${r.id}-${s}`}
                      title={isBackward ? "Retroceder (pedirá confirmación)" : ""}
                      className={`flex flex-col items-center py-2 px-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all ${
                        active
                          ? "bg-yellow-400 text-black border-yellow-400"
                          : isBackward
                            ? "bg-white/5 text-white/30 border-white/5 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/40"
                            : "bg-white/5 text-white/60 border-white/10 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <Icon className="w-4 h-4 mb-1" />
                      {STATUS_SHORT[s]}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-white/40 text-sm">Sin resultados</div>
        )}
      </div>
    </div>
  );
}

// ==================== ROOMS TAB ====================

function openDoorSheet(kind, id, action) {
  const query = new URLSearchParams({ type: kind, id });
  if (action) query.set("action", action);
  window.open(`/admin/logistica/hojas?${query.toString()}`, "_blank", "noopener,noreferrer");
}

function RoomsTab({ rooms, regs, regById, onChange }) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState(10);
  const [color, setColor] = useState(COLOR_PRESETS[0]);
  const [editing, setEditing] = useState(null); // room object

  const create = async () => {
    if (!name.trim()) return toast.error("Nombre requerido");
    try {
      await api.post("/admin/rooms", {
        name: name.trim(),
        capacity: Number(capacity) || 10,
        color: color || null,
      });
      toast.success("Habitación creada");
      setName(""); setCapacity(10); setColor(COLOR_PRESETS[0]); setCreating(false); onChange();
    } catch (e) {
      toast.error("No se pudo crear");
    }
  };

  const del = async (room) => {
    if (!window.confirm(`¿Eliminar "${room.name}"?`)) return;
    try {
      await api.delete(`/admin/rooms/${room.id}`);
      toast.success("Habitación eliminada");
      onChange();
    } catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-white/60">
          <span className="text-yellow-400 font-bold">{rooms.length}</span> habitación(es) creada(s)
        </div>
        <Button
          onClick={() => setCreating(true)}
          data-testid="create-room-btn"
          className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest text-xs"
        >
          <Plus className="w-4 h-4 mr-1" /> Nueva habitación
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rooms.map((room) => {
          const leaders = (room.leader_ids || []).map((id) => regById[id]).filter(Boolean);
          const occupants = (room.occupant_ids || []).map((id) => regById[id]).filter(Boolean);
          const used = occupants.length;
          const pct = (used / room.capacity) * 100;
          return (
            <div key={room.id} data-testid={`room-card-${room.id}`} className="bg-white/5 border border-white/10 rounded-xl p-4 relative overflow-hidden">
              {room.color && (
                <div
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-1.5"
                  style={{ background: room.color }}
                />
              )}
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0 flex items-center gap-2">
                  {room.color && (
                    <span
                      data-testid={`room-color-swatch-${room.id}`}
                      className="w-4 h-4 rounded-full border-2 border-white/30 flex-shrink-0"
                      style={{ background: room.color }}
                      title={`Color de brazalete: ${room.color}`}
                    />
                  )}
                  <div className="min-w-0">
                    <h3 className="font-heading uppercase text-lg text-yellow-400 tracking-wide truncate">{room.name}</h3>
                    <div className="text-xs text-white/50">{used}/{room.capacity} personas</div>
                  </div>
                </div>
                <button onClick={() => del(room)} data-testid={`room-delete-${room.id}`} className="text-white/40 hover:text-red-500 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-yellow-400 transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>

              {leaders.length > 0 && (
                <div className="mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-yellow-400/70 mb-1">Líderes</div>
                  <div className="flex flex-wrap gap-1">
                    {leaders.map((l) => (
                      <span key={l.id} className="px-2 py-0.5 rounded-full text-xs bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 font-semibold">
                        {l.full_name.split(" ")[0]}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Ocupantes</div>
                <div className="flex flex-wrap gap-1">
                  {occupants.length === 0 && <span className="text-white/40 text-xs italic">Sin ocupantes</span>}
                  {occupants.map((o) => (
                    <span
                      key={o.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-white/10 border border-white/10 text-white/80"
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: o.color || "#525252" }} />
                      {o.full_name.split(" ")[0]}
                    </span>
                  ))}
                </div>
              </div>

              <Button
                onClick={() => setEditing(room)}
                data-testid={`room-edit-${room.id}`}
                variant="outline"
                size="sm"
                className="w-full border-yellow-400/40 text-yellow-400 hover:bg-yellow-400 hover:text-black bg-transparent font-bold uppercase text-xs"
              >
                Asignar personas
              </Button>
              <div className="grid grid-cols-3 gap-1.5 mt-2">
                <Button onClick={() => openDoorSheet("room", room.id)} data-testid={`room-sheet-view-${room.id}`} variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 px-1">
                  <Eye className="w-3.5 h-3.5 mr-1" /> Ver
                </Button>
                <Button onClick={() => openDoorSheet("room", room.id, "print")} data-testid={`room-print-${room.id}`} variant="ghost" size="sm" className="text-yellow-400 hover:text-black hover:bg-yellow-400 px-1">
                  <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir
                </Button>
                <Button onClick={() => openDoorSheet("room", room.id, "pdf")} data-testid={`room-pdf-${room.id}`} variant="ghost" size="sm" className="text-pink-300 hover:text-white hover:bg-pink-600 px-1">
                  <Download className="w-3.5 h-3.5 mr-1" /> PDF
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {rooms.length === 0 && (
        <div className="text-center py-16 text-white/40 text-sm">
          Aún no hay habitaciones. Crea la primera con &quot;Nueva habitación&quot;.
        </div>
      )}

      {/* Create modal */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-neutral-900 border border-yellow-400/30 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-xl">Nueva habitación</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60 mb-1 block">Nombre</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Cabaña 1"
                data-testid="room-name-input"
                className="bg-white/5 border-white/10 !text-white"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60 mb-1 block">Capacidad</label>
              <Input
                type="number"
                min="1"
                max="100"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                data-testid="room-capacity-input"
                className="bg-white/5 border-white/10 !text-white"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60 mb-1 block flex items-center justify-between">
                <span>Color del grupo · brazalete</span>
                {color && (
                  <span className="normal-case tracking-normal text-white/70 flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full border border-white/30" style={{ background: color }} />
                    {color}
                  </span>
                )}
              </label>
              <div className="flex flex-wrap gap-2 p-2 bg-white/5 border border-white/10 rounded-lg">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`room-color-preset-${c}`}
                    onClick={() => setColor(c)}
                    style={{ background: c }}
                    className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${color === c ? "border-white ring-2 ring-yellow-400" : "border-white/20"}`}
                    aria-label={`Elegir color ${c}`}
                  />
                ))}
                <button
                  type="button"
                  data-testid="room-color-clear"
                  onClick={() => setColor("")}
                  className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-[10px] text-white/70 hover:bg-white/10 ${!color ? "border-white ring-2 ring-yellow-400" : "border-white/20"}`}
                  title="Sin color"
                >
                  ✕
                </button>
              </div>
              <p className="text-[10px] text-white/40 mt-1">
                Todos los ocupantes de esta habitación recibirán este color automáticamente al asignarlos.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setCreating(false)} className="text-white/70">Cancelar</Button>
            <Button
              onClick={create}
              data-testid="room-create-save"
              className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest"
            >
              Crear
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit assignment modal */}
      {editing && (
        <RoomAssignModal
          room={editing}
          allRegs={regs}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChange(); }}
        />
      )}
    </div>
  );
}

function RoomAssignModal({ room, allRegs, onClose, onSaved, endpoint }) {
  const [leaderIds, setLeaderIds] = useState(room.leader_ids || []);
  const [occupantIds, setOccupantIds] = useState(room.occupant_ids || []);
  const [roomColor, setRoomColor] = useState(room.color || "");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const patchUrl = endpoint || `/admin/rooms/${room.id}`;

  const availableList = allRegs
    .filter((r) => !q || r.full_name.toLowerCase().includes(q.toLowerCase()) || r.phone.includes(q))
    .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));

  const toggleOccupant = (id) => {
    setOccupantIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setLeaderIds((prev) => prev.filter((x) => x !== id));
  };
  const toggleLeader = (id) => {
    setLeaderIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    if (!occupantIds.includes(id)) setOccupantIds((prev) => [...prev, id]);
  };

  const save = async () => {
    if (occupantIds.length > room.capacity) {
      toast.error(`Máximo ${room.capacity} personas`);
      return;
    }
    setSaving(true);
    try {
      await api.patch(patchUrl, {
        leader_ids: leaderIds,
        occupant_ids: occupantIds,
        color: roomColor,
      });
      toast.success("Asignación guardada");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-neutral-900 border border-yellow-400/30 text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-xl flex items-center gap-2">
            {roomColor && (
              <span
                data-testid="room-assign-color-swatch"
                className="w-5 h-5 rounded-full border-2 border-white/40 flex-shrink-0"
                style={{ background: roomColor }}
              />
            )}
            {room.name}
          </DialogTitle>
          <div className="text-xs text-white/60">
            {occupantIds.length}/{room.capacity} ocupantes · {leaderIds.length} líder(es)
          </div>
        </DialogHeader>

        <div className="bg-white/5 border border-white/10 rounded-lg p-2 mb-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-widest text-white/60">
              Color del grupo · brazalete
            </span>
            <span className="text-[10px] text-white/40">
              Se aplica a todos los ocupantes al guardar
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {COLOR_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`room-assign-color-${c}`}
                onClick={() => setRoomColor(c)}
                style={{ background: c }}
                className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${roomColor === c ? "border-white ring-2 ring-yellow-400" : "border-white/20"}`}
                aria-label={`Elegir color ${c}`}
              />
            ))}
            <button
              type="button"
              data-testid="room-assign-color-clear"
              onClick={() => setRoomColor("")}
              className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-[10px] text-white/70 hover:bg-white/10 ${!roomColor ? "border-white ring-2 ring-yellow-400" : "border-white/20"}`}
              title="Sin color"
            >
              ✕
            </button>
          </div>
        </div>

        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar campista…"
          data-testid="room-assign-search"
          className="bg-white/5 border-white/10 !text-white mb-2"
        />
        <div className="overflow-y-auto flex-1 -mx-2 px-2">
          <div className="space-y-1">
            {availableList.map((r) => {
              const isOccupant = occupantIds.includes(r.id);
              const isLeader = leaderIds.includes(r.id);
              return (
                <div
                  key={r.id}
                  data-testid={`room-assign-row-${r.id}`}
                  className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${
                    isOccupant ? "bg-yellow-400/10 border-yellow-400/40" : "bg-white/5 border-white/10"
                  }`}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: r.color || "#525252" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{r.full_name}</div>
                    <div className="text-[10px] text-white/50">{r.camper_number_display} · {r.age_category}</div>
                  </div>
                  <button
                    onClick={() => toggleLeader(r.id)}
                    data-testid={`room-toggle-leader-${r.id}`}
                    className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest ${
                      isLeader ? "bg-yellow-400 text-black" : "bg-white/5 text-white/50 hover:bg-white/10"
                    }`}
                  >
                    Líder
                  </button>
                  <button
                    onClick={() => toggleOccupant(r.id)}
                    data-testid={`room-toggle-occupant-${r.id}`}
                    className={`px-3 py-1 rounded text-[10px] font-bold uppercase tracking-widest ${
                      isOccupant ? "bg-green-500 text-white" : "bg-white/5 text-white/50 hover:bg-white/10"
                    }`}
                  >
                    {isOccupant ? <CheckCircle2 className="w-3 h-3" /> : "Añadir"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
          <Button variant="ghost" onClick={onClose} className="text-white/70">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="room-assign-save" className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== CABINS TAB (VIP / private) ====================
// Mirrors RoomsTab but uses the /admin/cabins collection and a pink accent
// so it's visually distinct from the shared bunk rooms.
function CabinsTab({ cabins, regs, regById, onChange }) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState(6);
  const [color, setColor] = useState("#ec4899");
  const [editing, setEditing] = useState(null); // cabin object

  const create = async () => {
    if (!name.trim()) return toast.error("Nombre requerido");
    try {
      await api.post("/admin/cabins", {
        name: name.trim(),
        capacity: Number(capacity) || 6,
        color: color || null,
      });
      toast.success("Cabaña creada");
      setName(""); setCapacity(6); setColor("#ec4899"); setCreating(false); onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    }
  };

  const del = async (cabin) => {
    if (!window.confirm(`¿Eliminar la cabaña "${cabin.name}"?`)) return;
    try {
      await api.delete(`/admin/cabins/${cabin.id}`);
      toast.success("Cabaña eliminada");
      onChange();
    } catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <div className="text-sm text-white/60">
          <span className="text-pink-400 font-bold">{cabins.length}</span> cabaña(s) VIP creada(s) ·{" "}
          <span className="text-white/40 text-xs">privadas para familias</span>
        </div>
        <Button
          onClick={() => setCreating(true)}
          data-testid="create-cabin-btn"
          className="bg-pink-500 hover:bg-pink-400 text-black font-bold uppercase tracking-widest text-xs"
        >
          <Plus className="w-4 h-4 mr-1" /> Nueva cabaña
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {cabins.map((cabin) => {
          const leaders = (cabin.leader_ids || []).map((id) => regById[id]).filter(Boolean);
          const occupants = (cabin.occupant_ids || []).map((id) => regById[id]).filter(Boolean);
          const used = occupants.length;
          const pct = (used / cabin.capacity) * 100;
          return (
            <div key={cabin.id} data-testid={`cabin-card-${cabin.id}`} className="bg-white/5 border border-pink-400/20 rounded-xl p-4 relative overflow-hidden">
              {cabin.color && (
                <div
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-1.5"
                  style={{ background: cabin.color }}
                />
              )}
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-pink-400 flex-shrink-0" />
                  {cabin.color && (
                    <span
                      data-testid={`cabin-color-swatch-${cabin.id}`}
                      className="w-4 h-4 rounded-full border-2 border-white/30 flex-shrink-0"
                      style={{ background: cabin.color }}
                      title={`Color: ${cabin.color}`}
                    />
                  )}
                  <div className="min-w-0">
                    <h3 className="font-heading uppercase text-lg text-pink-300 tracking-wide truncate">{cabin.name}</h3>
                    <div className="text-xs text-white/50">{used}/{cabin.capacity} personas · VIP</div>
                  </div>
                </div>
                <button onClick={() => del(cabin)} data-testid={`cabin-delete-${cabin.id}`} className="text-white/40 hover:text-red-500 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-pink-400 transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>

              {leaders.length > 0 && (
                <div className="mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-pink-300/80 mb-1">Encargados</div>
                  <div className="flex flex-wrap gap-1">
                    {leaders.map((l) => (
                      <span key={l.id} className="px-2 py-0.5 rounded-full text-xs bg-pink-400/20 border border-pink-400/40 text-pink-200 font-semibold">
                        {l.full_name.split(" ")[0]}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Integrantes</div>
                <div className="flex flex-wrap gap-1">
                  {occupants.length === 0 && <span className="text-white/40 text-xs italic">Sin integrantes</span>}
                  {occupants.map((o) => (
                    <span
                      key={o.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-white/10 border border-white/10 text-white/80"
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: o.color || "#525252" }} />
                      {o.full_name.split(" ")[0]}
                    </span>
                  ))}
                </div>
              </div>

              <Button
                onClick={() => setEditing(cabin)}
                data-testid={`cabin-edit-${cabin.id}`}
                variant="outline"
                size="sm"
                className="w-full border-pink-400/40 text-pink-300 hover:bg-pink-400 hover:text-black bg-transparent font-bold uppercase text-xs"
              >
                Asignar integrantes
              </Button>
              <div className="grid grid-cols-3 gap-1.5 mt-2">
                <Button onClick={() => openDoorSheet("cabin", cabin.id)} data-testid={`cabin-sheet-view-${cabin.id}`} variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 px-1">
                  <Eye className="w-3.5 h-3.5 mr-1" /> Ver
                </Button>
                <Button onClick={() => openDoorSheet("cabin", cabin.id, "print")} data-testid={`cabin-print-${cabin.id}`} variant="ghost" size="sm" className="text-pink-300 hover:text-black hover:bg-pink-400 px-1">
                  <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir
                </Button>
                <Button onClick={() => openDoorSheet("cabin", cabin.id, "pdf")} data-testid={`cabin-pdf-${cabin.id}`} variant="ghost" size="sm" className="text-yellow-400 hover:text-black hover:bg-yellow-400 px-1">
                  <Download className="w-3.5 h-3.5 mr-1" /> PDF
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {cabins.length === 0 && (
        <div className="text-center py-16 text-white/40 text-sm">
          Aún no hay cabañas VIP. Crea la primera con &quot;Nueva cabaña&quot;.
          <div className="mt-2 text-[10px] text-white/30 uppercase tracking-widest">
            Ideal para familias que se quedan en cabañas privadas.
          </div>
        </div>
      )}

      {/* Create modal */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-neutral-900 border border-pink-400/30 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-pink-400" /> Nueva cabaña VIP
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60 mb-1 block">Nombre</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Cabaña Familia Rosario"
                data-testid="cabin-name-input"
                className="bg-white/5 border-white/10 !text-white"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60 mb-1 block">Capacidad</label>
              <Input
                type="number"
                min="1"
                max="100"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                data-testid="cabin-capacity-input"
                className="bg-white/5 border-white/10 !text-white"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60 mb-1 block flex items-center justify-between">
                <span>Color de la cabaña · brazalete</span>
                {color && (
                  <span className="normal-case tracking-normal text-white/70 flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full border border-white/30" style={{ background: color }} />
                    {color}
                  </span>
                )}
              </label>
              <div className="flex flex-wrap gap-2 p-2 bg-white/5 border border-white/10 rounded-lg">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`cabin-color-preset-${c}`}
                    onClick={() => setColor(c)}
                    style={{ background: c }}
                    className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${color === c ? "border-white ring-2 ring-pink-400" : "border-white/20"}`}
                    aria-label={`Elegir color ${c}`}
                  />
                ))}
                <button
                  type="button"
                  data-testid="cabin-color-clear"
                  onClick={() => setColor("")}
                  className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-[10px] text-white/70 hover:bg-white/10 ${!color ? "border-white ring-2 ring-pink-400" : "border-white/20"}`}
                  title="Sin color"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setCreating(false)} className="text-white/70">Cancelar</Button>
            <Button
              onClick={create}
              data-testid="cabin-create-save"
              className="bg-pink-500 hover:bg-pink-400 text-black font-bold uppercase tracking-widest"
            >
              Crear
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assignment modal — reuses RoomAssignModal against /admin/cabins */}
      {editing && (
        <RoomAssignModal
          room={editing}
          allRegs={regs}
          endpoint={`/admin/cabins/${editing.id}`}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChange(); }}
        />
      )}
    </div>
  );
}

// ==================== CLEANUP / UNASSIGNED + DUPLICATES TAB ====================
// Two-in-one report tab:
//  1) People not assigned to any room OR cabin (so leaders know who to place)
//  2) Potential duplicates detected server-side by normalized name / phone /
//     email. Admin can delete the extras directly.
function CleanupTab({ rooms, cabins, regs, onChange, isAdmin }) {
  const [loading, setLoading] = useState(true);
  const [unassigned, setUnassigned] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [skippedFamily, setSkippedFamily] = useState(0);
  const [familyPairs, setFamilyPairs] = useState([]);
  const [showFamilies, setShowFamilies] = useState(false);
  const [query, setQuery] = useState("");
  const [assigning, setAssigning] = useState(null); // person object
  const [merging, setMerging] = useState(null); // {keep, remove}

  const load = async () => {
    setLoading(true);
    try {
      const [u, d] = await Promise.all([
        api.get("/admin/logistics/unassigned"),
        api.get("/admin/logistics/duplicates"),
      ]);
      setUnassigned(u.data.people || []);
      setDuplicates(d.data.groups || []);
      setSkippedFamily(d.data.skipped_family || 0);
      setFamilyPairs(d.data.family_pairs || []);
    } catch (e) {
      toast.error("No se pudo cargar el reporte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const deleteDuplicate = async (person) => {
    if (!isAdmin) return toast.error("Solo admin puede eliminar");
    const label = `${person.full_name} (${person.camper_number_display || "#"})`;
    if (!window.confirm(`¿Eliminar a "${label}"?\n\nEsta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/registrations/${person.id}`);
      toast.success(`Eliminado: ${label}`);
      await load();
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const filteredUnassigned = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return unassigned;
    return unassigned.filter((p) =>
      (p.full_name || "").toLowerCase().includes(q) ||
      (p.phone || "").includes(q) ||
      (p.camper_number_display || "").toLowerCase().includes(q),
    );
  }, [unassigned, query]);

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-white/50">
        <Loader2 className="w-6 h-6 animate-spin text-orange-400 mr-2" /> Cargando reporte…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white/5 border border-white/10 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-white/50">Total inscritos</div>
          <div className="font-heading text-2xl text-white">{regs.length}</div>
        </div>
        <div className="bg-orange-500/10 border border-orange-400/30 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-orange-300">Sin asignar</div>
          <div className="font-heading text-2xl text-orange-300">{unassigned.length}</div>
        </div>
        <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-yellow-300">Habitaciones</div>
          <div className="font-heading text-2xl text-yellow-300">{rooms.length}</div>
        </div>
        <div className="bg-pink-500/10 border border-pink-400/30 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-pink-300">Cabañas VIP</div>
          <div className="font-heading text-2xl text-pink-300">{cabins.length}</div>
        </div>
      </div>

      {/* ==================== UNASSIGNED ==================== */}
      <section className="bg-white/5 border border-orange-400/20 rounded-2xl p-4 sm:p-6" data-testid="cleanup-unassigned-section">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-orange-300">Sin ubicar</div>
              <h3 className="font-heading uppercase text-xl text-white">Personas sin cabaña ni habitación</h3>
            </div>
          </div>
          <div className="text-sm">
            <span className="text-orange-300 font-bold text-2xl">{unassigned.length}</span>
            <span className="text-white/50 ml-1">pendiente(s)</span>
          </div>
        </div>

        {unassigned.length === 0 ? (
          <div className="text-center py-8 text-green-400/80 text-sm">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2" />
            ¡Todo el mundo está ubicado! 🎉
          </div>
        ) : (
          <>
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nombre, teléfono o #…"
                data-testid="cleanup-search"
                className="bg-white/5 border-white/10 !text-white pl-9"
              />
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-[480px] overflow-y-auto">
              {filteredUnassigned.map((p) => (
                <div
                  key={p.id}
                  data-testid={`unassigned-row-${p.id}`}
                  className="flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-lg p-2"
                >
                  <span className="w-2 h-8 rounded" style={{ background: p.color || "#525252" }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white text-sm truncate">{p.full_name}</div>
                    <div className="text-[10px] text-white/50 truncate">
                      {p.camper_number_display} · {p.age_category === "adult" ? "12+" : p.age_category === "child" ? "7-11" : "3-6"}
                      {p.phone ? ` · ${p.phone}` : ""}
                      {p.is_credit ? " · CRÉDITO" : ""}
                    </div>
                  </div>
                  <button
                    onClick={() => setAssigning(p)}
                    data-testid={`unassigned-assign-${p.id}`}
                    className="text-[10px] uppercase tracking-widest font-bold text-orange-300 hover:bg-orange-400 hover:text-black px-2 py-1 rounded border border-orange-400/40"
                  >
                    Asignar
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ==================== DUPLICATES ==================== */}
      <section className="bg-white/5 border border-red-400/20 rounded-2xl p-4 sm:p-6" data-testid="cleanup-duplicates-section">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Copy className="w-5 h-5 text-red-400" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-red-300">Posibles repetidos</div>
              <h3 className="font-heading uppercase text-xl text-white">Duplicados detectados</h3>
            </div>
          </div>
          <div className="text-sm">
            <span className="text-red-300 font-bold text-2xl">{duplicates.length}</span>
            <span className="text-white/50 ml-1">grupo(s)</span>
          </div>
        </div>

        {duplicates.length === 0 ? (
          <div className="text-center py-8 text-green-400/80 text-sm">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2" />
            Sin duplicados detectados.
            {skippedFamily > 0 && (
              <div className="mt-2 text-[11px] text-white/50" data-testid="skipped-family-empty">
                {skippedFamily} par(es) filtrado(s) automáticamente como familia (mismo teléfono, distinta edad o apellido compartido).
              </div>
            )}
          </div>
        ) : (
          <>
            <p className="text-[11px] text-white/50 mb-3">
              Detectados por <b>nombre normalizado</b>, <b>teléfono</b> o <b>email</b> compartido.
              El primero de cada grupo es el sugerido para <b>conservar</b> (más pagado / con contacto).
              {skippedFamily > 0 && (
                <span className="text-green-300" data-testid="skipped-family-hint">
                  {" · "}
                  <b>{skippedFamily}</b> par(es) reconocido(s) como familia (padre+hijo/hermanos) y omitidos.
                </span>
              )}
              {!isAdmin && <span className="text-red-300"> Solo un admin puede eliminar.</span>}
            </p>
            <div className="space-y-3">
              {duplicates.map((g) => (
                <div key={g.id} data-testid={`duplicate-group-${g.id}`} className="bg-black/40 border border-red-400/20 rounded-xl p-3">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-[10px] uppercase tracking-widest text-red-300 font-bold">
                      Grupo · {g.count} personas
                    </span>
                    {g.reasons.map((r) => (
                      <span key={r} className="text-[10px] uppercase tracking-widest bg-red-400/10 text-red-200 border border-red-400/30 px-2 py-0.5 rounded-full">
                        {r}
                      </span>
                    ))}
                  </div>
                  <div className="space-y-1">
                    {g.members.map((m, idx) => (
                      <div
                        key={m.id}
                        data-testid={`duplicate-member-${m.id}`}
                        className={`flex items-center gap-2 rounded-lg p-2 border ${
                          idx === 0
                            ? "bg-green-500/10 border-green-400/30"
                            : "bg-white/[0.03] border-white/10"
                        }`}
                      >
                        {idx === 0 ? (
                          <span className="text-[9px] uppercase tracking-widest font-bold bg-green-500 text-black px-1.5 py-0.5 rounded">
                            Conservar
                          </span>
                        ) : (
                          <span className="text-[9px] uppercase tracking-widest font-bold text-red-300">
                            Duplicado
                          </span>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-white text-sm truncate">
                            {m.full_name}{" "}
                            <span className="text-[10px] text-white/40 font-normal">
                              {m.camper_number_display}
                            </span>
                          </div>
                          <div className="text-[10px] text-white/50 truncate">
                            {m.phone || "sin tel"} · {m.email || "sin email"}
                            {(m.paid_amount || 0) > 0 ? ` · pagó $${m.paid_amount}` : ""}
                            {m.room_id ? " · en habitación" : ""}
                            {m.cabin_id ? " · en cabaña" : ""}
                          </div>
                        </div>
                        {idx > 0 && isAdmin && (
                          <div className="flex flex-col sm:flex-row gap-1">
                            <button
                              onClick={() => setMerging({ keep: g.members[0], remove: m })}
                              data-testid={`duplicate-merge-${m.id}`}
                              className="text-[10px] uppercase tracking-widest font-bold text-green-300 hover:bg-green-500 hover:text-black px-2 py-1 rounded border border-green-400/40 flex items-center gap-1"
                              title="Sumar pagos al conservado y luego eliminar"
                            >
                              <Sparkles className="w-3 h-3" /> Fusionar
                            </button>
                            <button
                              onClick={() => deleteDuplicate(m)}
                              data-testid={`duplicate-delete-${m.id}`}
                              className="text-[10px] uppercase tracking-widest font-bold text-red-300 hover:bg-red-500 hover:text-white px-2 py-1 rounded border border-red-400/40 flex items-center gap-1"
                            >
                              <Trash2 className="w-3 h-3" /> Eliminar
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ==================== FAMILIES (auto-filtered pairs) ==================== */}
      {familyPairs.length > 0 && (
        <section className="bg-white/5 border border-green-400/20 rounded-2xl p-4 sm:p-6" data-testid="cleanup-families-section">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-green-400" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-green-300">Familias omitidas</div>
                <h3 className="font-heading uppercase text-xl text-white">Pares detectados como familia</h3>
              </div>
            </div>
            <button
              onClick={() => setShowFamilies((v) => !v)}
              data-testid="toggle-families-btn"
              className="text-[11px] uppercase tracking-widest font-bold text-green-300 hover:bg-green-400 hover:text-black px-3 py-1.5 rounded border border-green-400/40"
            >
              {showFamilies ? "Ocultar" : `Mostrar ${familyPairs.length}`}
            </button>
          </div>
          <p className="text-[11px] text-white/50 mb-3">
            Comparten teléfono pero NO nombre ni email. Se omitieron del reporte de duplicados
            porque parecen padre+hijo o hermanos. Si alguno es de verdad un duplicado, avísame.
          </p>
          {showFamilies && (
            <div className="space-y-2">
              {familyPairs.map((fp, i) => (
                <div key={i} data-testid={`family-pair-${i}`} className="bg-black/40 border border-green-400/20 rounded-xl p-3">
                  <div className="text-[10px] uppercase tracking-widest text-green-300 font-bold mb-2">
                    {fp.reason} · mismo teléfono
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {fp.members.map((m) => (
                      <div key={m.id} className="flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-lg p-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-white text-sm truncate">
                            {m.full_name}{" "}
                            <span className="text-[10px] text-white/40 font-normal">
                              {m.camper_number_display}
                            </span>
                          </div>
                          <div className="text-[10px] text-white/50 truncate">
                            {m.age_category === "adult" ? "adulto" : m.age_category === "child" ? "niño 7-11" : "peque 3-6"}
                            {m.phone ? ` · ${m.phone}` : ""}
                            {(m.paid_amount || 0) > 0 ? ` · pagó $${m.paid_amount}` : ""}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Assign modal (unassigned person → pick room OR cabin) */}
      {assigning && (
        <QuickAssignModal
          person={assigning}
          rooms={rooms}
          cabins={cabins}
          onClose={() => setAssigning(null)}
          onSaved={() => { setAssigning(null); load(); onChange(); }}
        />
      )}

      {/* Merge modal (combine duplicate pagos into keeper) */}
      {merging && (
        <MergeDuplicateModal
          keep={merging.keep}
          remove={merging.remove}
          onClose={() => setMerging(null)}
          onSaved={() => { setMerging(null); load(); onChange(); }}
        />
      )}
    </div>
  );
}

// Small modal: pick a room OR cabin to add this person to.
function QuickAssignModal({ person, rooms, cabins, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);

  const assign = async (kind, item) => {
    setSaving(true);
    try {
      const path = kind === "cabin" ? "cabins" : "rooms";
      const newOccupants = [...(item.occupant_ids || []), person.id];
      await api.patch(`/admin/${path}/${item.id}`, { occupant_ids: newOccupants });
      toast.success(`${person.full_name} → ${item.name}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo asignar");
    } finally {
      setSaving(false);
    }
  };

  const available = (item) => (item.capacity || 0) - (item.occupant_ids?.length || 0);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-neutral-900 border border-orange-400/30 text-white max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-lg">
            Ubicar a <span className="text-orange-300">{person.full_name}</span>
          </DialogTitle>
          <div className="text-[11px] text-white/50">
            {person.camper_number_display} · Elige una habitación o cabaña con cupo
          </div>
        </DialogHeader>
        <div className="overflow-y-auto -mx-2 px-2 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-yellow-400/70 mb-1">Habitaciones</div>
            <div className="space-y-1">
              {rooms.length === 0 && <div className="text-white/40 text-xs italic">Sin habitaciones</div>}
              {rooms.map((r) => {
                const cupo = available(r);
                return (
                  <button
                    key={r.id}
                    disabled={cupo <= 0 || saving}
                    onClick={() => assign("room", r)}
                    data-testid={`quick-assign-room-${r.id}`}
                    className="w-full text-left flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/10 hover:border-yellow-400/60 hover:bg-yellow-400/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {r.color && <span className="w-3 h-3 rounded-full border border-white/30" style={{ background: r.color }} />}
                    <Bed className="w-4 h-4 text-yellow-400/70" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-white truncate">{r.name}</div>
                      <div className="text-[10px] text-white/50">
                        {r.occupant_ids?.length || 0}/{r.capacity} · {cupo > 0 ? `${cupo} cupo(s)` : "lleno"}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-pink-300 mb-1">Cabañas VIP</div>
            <div className="space-y-1">
              {cabins.length === 0 && <div className="text-white/40 text-xs italic">Sin cabañas</div>}
              {cabins.map((c) => {
                const cupo = available(c);
                return (
                  <button
                    key={c.id}
                    disabled={cupo <= 0 || saving}
                    onClick={() => assign("cabin", c)}
                    data-testid={`quick-assign-cabin-${c.id}`}
                    className="w-full text-left flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/10 hover:border-pink-400/60 hover:bg-pink-400/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {c.color && <span className="w-3 h-3 rounded-full border border-white/30" style={{ background: c.color }} />}
                    <Sparkles className="w-4 h-4 text-pink-400/70" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-white truncate">{c.name}</div>
                      <div className="text-[10px] text-white/50">
                        {c.occupant_ids?.length || 0}/{c.capacity} · {cupo > 0 ? `${cupo} cupo(s)` : "lleno"}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
          <Button variant="ghost" onClick={onClose} className="text-white/70">Cerrar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Merge-duplicate modal: transfer paid amounts + contact info from `remove`
// into `keep`, then delete `remove`. Server does the heavy lifting.
function MergeDuplicateModal({ keep, remove, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const removePaid = Number(remove?.paid_amount || 0);
  const keepPaid = Number(keep?.paid_amount || 0);
  const combined = removePaid + keepPaid;
  const keepTotal = Number(keep?.total_amount || 0);
  const cappedCombined = Math.min(combined, keepTotal);

  const doMerge = async () => {
    setSaving(true);
    try {
      const res = await api.post("/admin/registrations/merge", {
        keep_id: keep.id,
        remove_id: remove.id,
      });
      const abs = res.data?.absorbed || 0;
      const left = res.data?.leftover_unapplied || 0;
      toast.success(
        `Fusionado ✓ absorbió $${abs.toFixed(2)}` +
        (left > 0 ? ` (sobrante $${left.toFixed(2)} no aplicado)` : "")
      );
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo fusionar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-neutral-900 border border-green-400/30 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-green-400" /> Fusionar duplicado
          </DialogTitle>
          <div className="text-[11px] text-white/50">
            Los pagos y datos de contacto pasan al conservado antes de eliminar.
          </div>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-green-500/10 border border-green-400/30 rounded-lg p-3" data-testid="merge-keep-info">
            <div className="text-[10px] uppercase tracking-widest text-green-300 font-bold mb-1">
              Conservar
            </div>
            <div className="font-bold text-white">{keep.full_name} <span className="text-white/40 text-xs font-normal">{keep.camper_number_display}</span></div>
            <div className="text-[11px] text-white/60 mt-1">
              Pagado actual: <b className="text-white">${keepPaid.toFixed(2)}</b> / ${keepTotal.toFixed(2)}
            </div>
          </div>

          <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-3" data-testid="merge-remove-info">
            <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold mb-1">
              Eliminar (aportará sus pagos)
            </div>
            <div className="font-bold text-white">{remove.full_name} <span className="text-white/40 text-xs font-normal">{remove.camper_number_display}</span></div>
            <div className="text-[11px] text-white/60 mt-1">
              Aportará: <b className="text-green-300">+${removePaid.toFixed(2)}</b>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">
              Resultado
            </div>
            <div className="text-sm text-white">
              Pagado total: <b className="text-green-300">${cappedCombined.toFixed(2)}</b>
              <span className="text-white/40"> / ${keepTotal.toFixed(2)}</span>
              {combined > keepTotal && (
                <span className="ml-2 text-[10px] text-orange-300">
                  (excedente ${(combined - keepTotal).toFixed(2)} queda como nota)
                </span>
              )}
            </div>
            <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mt-2">
              <div className="h-full bg-green-400" style={{ width: `${Math.min(100, keepTotal ? (cappedCombined / keepTotal) * 100 : 0)}%` }} />
            </div>
          </div>

          <div className="text-[10px] text-white/40">
            También se copian teléfono/email/emergencia si al conservado le faltan, y las
            transacciones de pago del duplicado se re-asignan al conservado.
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-2 border-t border-white/10">
          <Button variant="ghost" onClick={onClose} className="text-white/70" disabled={saving}>Cancelar</Button>
          <Button
            onClick={doMerge}
            disabled={saving}
            data-testid="merge-confirm-btn"
            className="bg-green-500 hover:bg-green-400 text-black font-bold uppercase tracking-widest"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Fusionar y eliminar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== VEHICLES TAB ====================
function VehiclesTab({ vehicles, regs, regById, onChange }) {
  const [creating, setCreating] = useState(false);
  const [editingInfo, setEditingInfo] = useState(null); // vehicle being edited (details, not passengers)
  const [editing, setEditing] = useState(null); // vehicle being edited (passengers)
  const [form, setForm] = useState({ driver_name: "", driver_phone: "", capacity: 4, vehicle_model: "", departure_time: "" });

  const create = async () => {
    if (!form.driver_name.trim()) return toast.error("Nombre del chofer requerido");
    try {
      await api.post("/admin/vehicles", { ...form, capacity: Number(form.capacity) || 4 });
      toast.success("Vehículo agregado");
      setForm({ driver_name: "", driver_phone: "", capacity: 4, vehicle_model: "", departure_time: "" });
      setCreating(false);
      onChange();
    } catch (e) {
      toast.error("No se pudo crear");
    }
  };
  const saveEdit = async () => {
    if (!editingInfo || !form.driver_name.trim()) return toast.error("Nombre del chofer requerido");
    try {
      await api.patch(`/admin/vehicles/${editingInfo.id}`, { ...form, capacity: Number(form.capacity) || 4 });
      toast.success("Datos actualizados");
      setEditingInfo(null);
      setForm({ driver_name: "", driver_phone: "", capacity: 4, vehicle_model: "", departure_time: "" });
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };
  const del = async (v) => {
    if (!window.confirm(`¿Eliminar el vehículo de ${v.driver_name}?`)) return;
    try {
      await api.delete(`/admin/vehicles/${v.id}`);
      toast.success("Eliminado");
      onChange();
    } catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-white/60">
          <span className="text-yellow-400 font-bold">{vehicles.length}</span> vehículo(s) registrado(s)
        </div>
        <Button
          onClick={() => setCreating(true)}
          data-testid="create-vehicle-btn"
          className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest text-xs"
        >
          <Plus className="w-4 h-4 mr-1" /> Agregar vehículo
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {vehicles.map((v) => {
          const passengers = (v.passenger_ids || []).map((id) => regById[id]).filter(Boolean);
          const pct = (passengers.length / v.capacity) * 100;
          return (
            <div key={v.id} data-testid={`vehicle-card-${v.id}`} className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <h3 className="font-heading uppercase text-lg text-yellow-400 tracking-wide truncate flex items-center gap-2">
                    <Car className="w-5 h-5" /> {v.driver_name}
                  </h3>
                  <div className="text-xs text-white/50">
                    {v.vehicle_model || "Sin modelo"} · {v.driver_phone || "sin teléfono"}
                    {v.departure_time ? ` · sale ${v.departure_time}` : ""}
                  </div>
                  <div className="text-xs text-white/60 mt-1">{passengers.length}/{v.capacity} pasajeros</div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setEditingInfo(v); setForm({ driver_name: v.driver_name || "", driver_phone: v.driver_phone || "", capacity: v.capacity || 4, vehicle_model: v.vehicle_model || "", departure_time: v.departure_time || "" }); }}
                    data-testid={`vehicle-edit-info-${v.id}`}
                    className="text-white/50 hover:text-yellow-400 p-1"
                    title="Editar datos del chofer/vehículo"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(v)} data-testid={`vehicle-delete-${v.id}`} className="text-white/40 hover:text-red-500 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-yellow-400 transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Pasajeros</div>
                <div className="flex flex-wrap gap-1">
                  {passengers.length === 0 && <span className="text-white/40 text-xs italic">Sin pasajeros</span>}
                  {passengers.map((p) => (
                    <span key={p.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-white/10 border border-white/10 text-white/80">
                      <span className="w-2 h-2 rounded-full" style={{ background: p.color || "#525252" }} />
                      {p.full_name.split(" ")[0]}
                    </span>
                  ))}
                </div>
              </div>
              <Button
                onClick={() => setEditing(v)}
                data-testid={`vehicle-edit-${v.id}`}
                variant="outline"
                size="sm"
                className="w-full border-yellow-400/40 text-yellow-400 hover:bg-yellow-400 hover:text-black bg-transparent font-bold uppercase text-xs"
              >
                Asignar pasajeros
              </Button>
            </div>
          );
        })}
      </div>

      {vehicles.length === 0 && (
        <div className="text-center py-16 text-white/40 text-sm">
          Aún no hay vehículos. Agrega uno con &quot;Agregar vehículo&quot;.
        </div>
      )}

      {/* Create modal */}
      <Dialog open={creating || !!editingInfo} onOpenChange={(v) => { if (!v) { setCreating(false); setEditingInfo(null); } }}>
        <DialogContent className="bg-neutral-900 border border-yellow-400/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-xl">
              {editingInfo ? "Editar vehículo" : "Agregar vehículo"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <VField label="Chofer">
              <Input
                value={form.driver_name}
                onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
                placeholder="Ej: Jeffrey"
                data-testid="vehicle-driver-name"
                className="bg-white/5 border-white/10 !text-white"
              />
            </VField>
            <VField label="Teléfono">
              <Input
                value={form.driver_phone}
                onChange={(e) => setForm({ ...form, driver_phone: e.target.value })}
                placeholder="Ej: 614-555-0100"
                data-testid="vehicle-driver-phone"
                className="bg-white/5 border-white/10 !text-white"
              />
            </VField>
            <div className="grid grid-cols-2 gap-2">
              <VField label="Modelo">
                <Input
                  value={form.vehicle_model}
                  onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })}
                  placeholder="Ej: Toyota Camry"
                  data-testid="vehicle-model"
                  className="bg-white/5 border-white/10 !text-white"
                />
              </VField>
              <VField label="Capacidad">
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                  data-testid="vehicle-capacity"
                  className="bg-white/5 border-white/10 !text-white"
                />
              </VField>
            </div>
            <VField label="Hora de salida (opcional)">
              <Input
                value={form.departure_time}
                onChange={(e) => setForm({ ...form, departure_time: e.target.value })}
                placeholder="Ej: Viernes 11:30 AM"
                data-testid="vehicle-departure"
                className="bg-white/5 border-white/10 !text-white"
              />
            </VField>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => { setCreating(false); setEditingInfo(null); }} className="text-white/70">Cancelar</Button>
            <Button
              onClick={editingInfo ? saveEdit : create}
              data-testid={editingInfo ? "vehicle-edit-save" : "vehicle-create-save"}
              className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest"
            >
              {editingInfo ? "Guardar cambios" : "Crear"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {editing && (
        <VehicleAssignModal
          vehicle={editing}
          allRegs={regs}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChange(); }}
        />
      )}
    </div>
  );
}

function VField({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/60 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function VehicleAssignModal({ vehicle, allRegs, onClose, onSaved }) {
  const [passengerIds, setPassengerIds] = useState(vehicle.passenger_ids || []);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const list = allRegs
    .filter((r) => !q || r.full_name.toLowerCase().includes(q.toLowerCase()) || r.phone.includes(q))
    .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));

  const toggle = (id) => {
    setPassengerIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const save = async () => {
    if (passengerIds.length > vehicle.capacity) return toast.error(`Máximo ${vehicle.capacity}`);
    setSaving(true);
    try {
      await api.patch(`/admin/vehicles/${vehicle.id}`, { passenger_ids: passengerIds });
      toast.success("Pasajeros guardados");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-neutral-900 border border-yellow-400/30 text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-xl">
            {vehicle.driver_name} · {passengerIds.length}/{vehicle.capacity}
          </DialogTitle>
        </DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar campista…" data-testid="vehicle-assign-search" className="bg-white/5 border-white/10 !text-white mb-2" />
        <div className="overflow-y-auto flex-1 -mx-2 px-2">
          <div className="space-y-1">
            {list.map((r) => {
              const isOn = passengerIds.includes(r.id);
              return (
                <div
                  key={r.id}
                  data-testid={`vehicle-assign-row-${r.id}`}
                  className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${
                    isOn ? "bg-yellow-400/10 border-yellow-400/40" : "bg-white/5 border-white/10"
                  }`}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: r.color || "#525252" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{r.full_name}</div>
                    <div className="text-[10px] text-white/50">{r.camper_number_display} · {r.age_category}</div>
                  </div>
                  <button
                    onClick={() => toggle(r.id)}
                    data-testid={`vehicle-toggle-${r.id}`}
                    className={`px-3 py-1 rounded text-[10px] font-bold uppercase tracking-widest ${
                      isOn ? "bg-green-500 text-white" : "bg-white/5 text-white/50 hover:bg-white/10"
                    }`}
                  >
                    {isOn ? <CheckCircle2 className="w-3 h-3" /> : "Añadir"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
          <Button variant="ghost" onClick={onClose} className="text-white/70">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="vehicle-assign-save" className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== COLORS TAB ====================
function ColorsTab({ regs, onChange }) {
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(null);

  const filtered = regs.filter((r) => !q || r.full_name.toLowerCase().includes(q.toLowerCase()));

  const setColor = async (reg, color) => {
    setSaving(reg.id);
    try {
      await api.patch(`/admin/registrations/${reg.id}/color`, { color });
      onChange();
    } catch { toast.error("Error"); } finally { setSaving(null); }
  };

  return (
    <div className="space-y-4">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar campista…"
        data-testid="colors-search"
        className="bg-white/5 border-white/10 !text-white"
      />
      <div className="grid gap-2">
        {filtered.map((r) => (
          <div key={r.id} data-testid={`color-row-${r.id}`} className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex-shrink-0 border-2 border-white/20" style={{ background: r.color || "#525252" }} />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-white text-sm truncate">{r.full_name}</div>
              <div className="text-[10px] text-white/50">{r.camper_number_display}</div>
            </div>
            <div className="flex flex-wrap gap-1 justify-end">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(r, c)}
                  data-testid={`color-preset-${r.id}-${c}`}
                  disabled={saving === r.id}
                  className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${r.color === c ? "border-white ring-2 ring-yellow-400" : "border-white/20"}`}
                  style={{ background: c }}
                  title={c}
                />
              ))}
              <button
                onClick={() => setColor(r, null)}
                data-testid={`color-clear-${r.id}`}
                disabled={saving === r.id}
                className="w-6 h-6 rounded-full bg-white/5 border-2 border-white/20 flex items-center justify-center hover:bg-white/10"
                title="Quitar color"
              >
                <X className="w-3 h-3 text-white/60" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== AUDIT TAB ====================
function AuditTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/checkin/events?limit=500");
      setEvents(data);
    } catch {
      toast.error("No se pudo cargar el historial");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const filtered = events.filter((e) => {
    if (sourceFilter !== "all" && e.source !== sourceFilter) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (e.full_name || "").toLowerCase().includes(s)
      || (e.camper_number_display || "").includes(s);
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre o # de campista…"
          data-testid="audit-search"
          className="bg-white/5 border-white/10 !text-white placeholder:text-white/40 sm:max-w-xs"
        />
        <div className="flex gap-1">
          {[
            { id: "all", label: "Todo" },
            { id: "admin", label: "Admin" },
            { id: "qr", label: "QR" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setSourceFilter(f.id)}
              data-testid={`audit-filter-${f.id}`}
              className={`px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider whitespace-nowrap border ${
                sourceFilter === f.id ? "bg-yellow-400 text-black border-yellow-400" : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="text-xs text-white/50 self-center ml-auto">
          <span className="text-yellow-400 font-bold">{filtered.length}</span> evento(s)
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-yellow-400 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-white/40 text-sm">Sin movimientos aún</div>
      ) : (
        <div className="space-y-1">
          {filtered.map((e) => {
            const Icon = STATUS_ICON[e.status_location] || UserIcon;
            const when = new Date(e.timestamp);
            const isQr = e.source === "qr";
            return (
              <div
                key={e.id}
                data-testid={`audit-row-${e.id}`}
                className={`flex items-center gap-3 p-3 border rounded-lg hover:bg-white/[0.07] transition-colors ${
                  e.deleted ? "bg-white/[0.02] border-white/5 opacity-70" : "bg-white/5 border-white/10"
                }`}
              >
                <span className={`w-2 h-8 rounded-full flex-shrink-0`} style={{ background: e.color || "#525252" }} />
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border ${STATUS_COLOR[e.status_location] || STATUS_COLOR.not_checked_in}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-yellow-400 font-mono text-xs">{e.camper_number_display}</span>
                    <span className={`font-bold text-sm truncate ${e.deleted ? "text-white/50 italic" : "text-white"}`}>{e.full_name || "?"}</span>
                    {e.deleted && <span className="text-[9px] uppercase tracking-widest text-red-400 border border-red-500/40 px-1.5 py-0.5 rounded">eliminado</span>}
                    <span className="text-white/50 text-xs">→</span>
                    <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_COLOR[e.status_location] || STATUS_COLOR.not_checked_in}`}>
                      {STATUS_SHORT[e.status_location] || e.status_location}
                    </span>
                  </div>
                  <div className="text-[10px] text-white/50 mt-0.5 flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                      isQr ? "bg-green-500/20 text-green-300 border border-green-500/40" : "bg-blue-500/20 text-blue-300 border border-blue-500/40"
                    }`}>
                      {isQr ? <QrCode className="w-2.5 h-2.5" /> : <ShieldCheck className="w-2.5 h-2.5" />}
                      {isQr ? "Escaneo QR" : (e.actor || "Admin")}
                    </span>
                    <span>{when.toLocaleString("es", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ==================== DEBTS TAB ====================
function DebtsTab({ regs, onChange }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("debt"); // debt | paid | all
  const [editingNote, setEditingNote] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const enhanced = regs.map((r) => ({
    ...r,
    _remaining: Math.max(0, (r.total_amount || 0) - (r.paid_amount || 0)),
  }));
  const filtered = enhanced.filter((r) => {
    if (filter === "paid" && r._remaining > 0.01) return false;
    if (filter === "debt" && r._remaining <= 0.01) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.full_name || "").toLowerCase().includes(s) ||
      (r.camper_number_display || "").includes(s) ||
      (r.phone || "").includes(s);
  }).sort((a, b) => b._remaining - a._remaining);

  const totalDebt = filtered.reduce((sum, r) => sum + r._remaining, 0);
  const totalPaid = filtered.reduce((sum, r) => sum + (r.paid_amount || 0), 0);
  const totalGoal = filtered.reduce((sum, r) => sum + (r.total_amount || 0), 0);

  const saveNote = async (regId) => {
    setSaving(true);
    try {
      await api.patch(`/admin/registrations/${regId}/debt-note`, { debt_note: noteDraft });
      toast.success("Nota guardada");
      setEditingNote(null);
      setNoteDraft("");
      onChange();
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3" data-testid="debts-total">
          <div className="text-[10px] uppercase tracking-widest text-red-300">Debe</div>
          <div className="font-heading text-2xl text-red-300 tabular-nums">${totalDebt.toFixed(0)}</div>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-green-300">Pagado</div>
          <div className="font-heading text-2xl text-green-300 tabular-nums">${totalPaid.toFixed(0)}</div>
        </div>
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-yellow-300">Meta total</div>
          <div className="font-heading text-2xl text-yellow-300 tabular-nums">${totalGoal.toFixed(0)}</div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          data-testid="debts-search"
          placeholder="Buscar por nombre, # o teléfono…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="bg-white/5 border-white/10 !text-white placeholder:text-white/40 sm:max-w-xs"
        />
        <div className="flex flex-wrap gap-1">
          {[
            { id: "debt", label: "Con deuda" },
            { id: "paid", label: "Pagados" },
            { id: "all", label: "Todos" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`debts-filter-${f.id}`}
              className={`px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border whitespace-nowrap ${
                filter === f.id ? "bg-yellow-400 text-black border-yellow-400" : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {filtered.map((r) => {
          const pct = r.total_amount ? Math.round(((r.paid_amount || 0) / r.total_amount) * 100) : 0;
          const isEditing = editingNote === r.id;
          return (
            <div key={r.id} data-testid={`debt-row-${r.id}`} className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="flex items-center gap-3">
                <span className="w-2 h-10 rounded-full flex-shrink-0" style={{ background: r.color || "#525252" }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-yellow-400 font-mono text-xs">{r.camper_number_display}</span>
                    <span className="font-bold text-white text-sm truncate">{r.full_name}</span>
                    {r.is_credit && (
                      <span data-testid={`credit-pill-${r.id}`} className="px-1.5 py-0.5 rounded-full bg-orange-500/20 border border-orange-400/50 text-orange-300 text-[9px] font-bold uppercase tracking-widest">
                        Crédito
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-white/50">{r.phone}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className={`text-lg font-bold tabular-nums ${r._remaining > 0.01 ? "text-red-300" : "text-green-300"}`}>
                    ${r._remaining.toFixed(0)}
                  </div>
                  <div className="text-[10px] text-white/50 tabular-nums">
                    ${(r.paid_amount || 0).toFixed(0)} / ${(r.total_amount || 0).toFixed(0)} · {pct}%
                  </div>
                </div>
              </div>
              <div className="mt-2 pl-5">
                {isEditing ? (
                  <div className="flex gap-1">
                    <Input
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder="Ej: Pagará el 15 de sept, faltan $200…"
                      data-testid={`debt-note-input-${r.id}`}
                      className="bg-white/5 border-white/10 !text-white text-xs h-7"
                      autoFocus
                    />
                    <Button size="sm" onClick={() => saveNote(r.id)} disabled={saving} data-testid={`debt-note-save-${r.id}`} className="h-7 bg-yellow-400 hover:bg-yellow-500 text-black text-xs">Guardar</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingNote(null); setNoteDraft(""); }} className="h-7 text-white/70">Cancelar</Button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setEditingNote(r.id); setNoteDraft(r.debt_note || ""); }}
                    data-testid={`debt-note-edit-${r.id}`}
                    className="text-left w-full text-[11px] text-white/60 italic hover:text-yellow-400 flex items-center gap-1"
                  >
                    📝 {r.debt_note || <span className="text-white/30">Agregar nota (ej: pagará en 15 días)</span>}
                    <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-white/40 text-sm">Sin resultados</div>
        )}
      </div>
    </div>
  );
}

// ==================== MANUAL ADD MODAL ====================
function ManualAddModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", emergency_contact: "",
    age_category: "adult", total_amount: "", payment_mode: "paid", plan_type: "weekly", debt_note: "",
  });
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setForm({ full_name: "", phone: "", email: "", emergency_contact: "",
      age_category: "adult", total_amount: "", payment_mode: "paid", plan_type: "weekly", debt_note: "" });
  };

  const save = async () => {
    if (!form.full_name.trim()) return toast.error("Nombre requerido");
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.total_amount) delete body.total_amount;
      else body.total_amount = parseFloat(body.total_amount);
      const { data } = await api.post("/admin/registrations/manual", body);
      toast.success(`Inscrito: ${data.full_name} (${data.camper_number_display})`);
      reset();
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    } finally { setSaving(false); }
  };

  const defaultTotals = { small: 55, child: 150, adult: 250 };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="bg-neutral-900 border border-yellow-400/30 text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-xl">Nuevo inscrito manual</DialogTitle>
          <div className="text-xs text-white/60">Se creará el # de campista y el QR automáticamente</div>
        </DialogHeader>
        <div className="space-y-3">
          <FormField label="Nombre completo *">
            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="manual-name" className="bg-white/5 border-white/10 !text-white" placeholder="Ej: Juan Pérez" autoFocus />
          </FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Teléfono">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="manual-phone" className="bg-white/5 border-white/10 !text-white" placeholder="614-555-0100" />
            </FormField>
            <FormField label="Categoría">
              <select value={form.age_category} onChange={(e) => setForm({ ...form, age_category: e.target.value })}
                data-testid="manual-age" className="w-full h-10 px-3 rounded-md bg-white/5 border border-white/10 text-white text-sm">
                <option value="small">3-6 años ($55)</option>
                <option value="child">7-11 años ($150)</option>
                <option value="adult">12+ años ($250)</option>
              </select>
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FormField label={`Total ($) — por defecto $${defaultTotals[form.age_category]}`}>
              <Input type="number" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
                data-testid="manual-total" className="bg-white/5 border-white/10 !text-white" placeholder={String(defaultTotals[form.age_category])} />
            </FormField>
            <FormField label="Modo de pago">
              <div className="grid grid-cols-3 gap-1 h-10">
                <button type="button" onClick={() => setForm({ ...form, payment_mode: "paid" })} data-testid="manual-mode-paid"
                  className={`rounded-md text-xs font-bold uppercase tracking-wider border ${form.payment_mode === "paid" ? "bg-green-500 border-green-500 text-white" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"}`}>
                  Pagado
                </button>
                <button type="button" onClick={() => setForm({ ...form, payment_mode: "credit" })} data-testid="manual-mode-credit"
                  className={`rounded-md text-xs font-bold uppercase tracking-wider border ${form.payment_mode === "credit" ? "bg-orange-500 border-orange-500 text-white" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"}`}>
                  Crédito
                </button>
                <button type="button" onClick={() => setForm({ ...form, payment_mode: "sponsored" })} data-testid="manual-mode-beca"
                  className={`rounded-md text-xs font-bold uppercase tracking-wider border ${form.payment_mode === "sponsored" ? "bg-pink-500 border-pink-500 text-white" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"}`}>
                  ★ Beca
                </button>
              </div>
            </FormField>
          </div>
          {form.payment_mode === "sponsored" && (
            <FormField label="Patrocinador (fuente de la beca)">
              <Input value={form.sponsor_source || "Recaudación Jóvenes"} onChange={(e) => setForm({ ...form, sponsor_source: e.target.value })}
                data-testid="manual-sponsor-source" placeholder="Recaudación Jóvenes"
                className="bg-white/5 border-white/10 !text-white" />
              <p className="text-[10px] text-pink-300 mt-1">Se debitará del presupuesto de becas al guardar.</p>
            </FormField>
          )}
          {form.payment_mode === "credit" && (
            <FormField label="Plan de pago (para el crédito)">
              <select value={form.plan_type} onChange={(e) => setForm({ ...form, plan_type: e.target.value })}
                data-testid="manual-plan" className="w-full h-10 px-3 rounded-md bg-white/5 border border-white/10 text-white text-sm">
                <option value="daily">Diario</option>
                <option value="weekly">Semanal</option>
                <option value="biweekly">Quincenal</option>
                <option value="monthly">Mensual</option>
              </select>
            </FormField>
          )}
          <FormField label="Contacto de emergencia">
            <Input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })}
              data-testid="manual-emergency" className="bg-white/5 border-white/10 !text-white" placeholder="Nombre + tel" />
          </FormField>
          <FormField label="Nota de deuda (opcional)">
            <Input value={form.debt_note} onChange={(e) => setForm({ ...form, debt_note: e.target.value })}
              data-testid="manual-debt-note" className="bg-white/5 border-white/10 !text-white" placeholder="Ej: Pagará en 2 semanas" />
          </FormField>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={saving} className="text-white/70">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="manual-save"
            className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear inscrito"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-white/60 mb-1 block">{label}</label>
      {children}
    </div>
  );
}


// ==================== USERS TAB (admin-only) ====================
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ username: "", password: "", display_name: "", role: "kiosko" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar los usuarios");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.username.trim() || !form.password.trim()) return toast.error("Usuario y contraseña requeridos");
    setCreating(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`Usuario ${form.role === "kiosko" ? "Kiosco" : "Logística"} creado`);
      setForm({ username: "", password: "", display_name: "", role: form.role });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    } finally { setCreating(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`¿Eliminar el usuario "${u.username}"?`)) return;
    try {
      await api.delete(`/admin/users/${u.username}`);
      toast.success("Usuario eliminado");
      load();
    } catch { toast.error("Error"); }
  };

  const roleLabel = (r) => (r === "logistica" ? "Logística" : "Kiosco");
  const roleColor = (r) => (r === "logistica" ? "bg-purple-500/20 text-purple-300" : "bg-blue-500/20 text-blue-300");
  const publicOrigin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="space-y-6">
      <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-yellow-400 flex-shrink-0" />
          <div className="text-sm text-white/80 min-w-0 flex-1">
            <div className="font-bold text-yellow-300 mb-2">Dos privilegios disponibles</div>
            <div className="space-y-3">
              <div>
                <div className="mb-1"><b className="text-blue-300">Kiosco</b> · escanea QR y avanza estados (llegó, en camino, salió). NO ve habitaciones ni vehículos.</div>
                <div className="flex flex-wrap gap-1 items-center">
                  <span className="text-[10px] uppercase tracking-widest text-white/50 mr-1">Link para compartir:</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${publicOrigin}/api/share/kiosko`); toast.success("Link copiado"); }}
                    data-testid="copy-kiosko-share"
                    className="font-mono text-xs text-yellow-200 bg-black/40 px-2 py-1 rounded hover:bg-black/60 border border-white/10 break-all text-left"
                  >
                    {publicOrigin}/api/share/kiosko
                  </button>
                </div>
              </div>
              <div>
                <div className="mb-1"><b className="text-purple-300">Logística</b> · crea/edita habitaciones (con color de brazalete) y vehículos con pasajeros. NO puede hacer check-in.</div>
                <div className="flex flex-wrap gap-1 items-center">
                  <span className="text-[10px] uppercase tracking-widest text-white/50 mr-1">Link para compartir:</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${publicOrigin}/api/share/logistica`); toast.success("Link copiado"); }}
                    data-testid="copy-logistica-share"
                    className="font-mono text-xs text-yellow-200 bg-black/40 px-2 py-1 rounded hover:bg-black/60 border border-white/10 break-all text-left"
                  >
                    {publicOrigin}/api/share/logistica
                  </button>
                </div>
              </div>
              <div className="text-[11px] text-white/50 pt-1 border-t border-white/10 mt-1">
                💡 Los links <code className="text-yellow-200">/api/share/…</code> muestran miniatura personalizada en WhatsApp y redirigen automáticamente al login.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create form */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-widest text-yellow-400/70 mb-3 font-bold">Nuevo usuario</div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <button
            type="button"
            data-testid="user-role-kiosko"
            onClick={() => setForm({ ...form, role: "kiosko" })}
            className={`px-3 py-2 rounded-lg border text-sm font-bold uppercase tracking-widest transition ${form.role === "kiosko" ? "bg-blue-500/30 border-blue-400 text-blue-100" : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10"}`}
          >
            Kiosco · Check-in
          </button>
          <button
            type="button"
            data-testid="user-role-logistica"
            onClick={() => setForm({ ...form, role: "logistica" })}
            className={`px-3 py-2 rounded-lg border text-sm font-bold uppercase tracking-widest transition ${form.role === "logistica" ? "bg-purple-500/30 border-purple-400 text-purple-100" : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10"}`}
          >
            Logística · Cabañas + Transporte
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
            placeholder="Usuario (ej: kiosko1)" data-testid="user-username" className="bg-white/5 border-white/10 !text-white" />
          <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Contraseña" data-testid="user-password" className="bg-white/5 border-white/10 !text-white" />
          <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            placeholder="Nombre visible (opcional)" data-testid="user-display" className="bg-white/5 border-white/10 !text-white" />
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={create} disabled={creating} data-testid="user-create-save"
            className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest text-xs">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Crear {roleLabel(form.role)}</>}
          </Button>
        </div>
      </div>

      {/* List */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 font-bold">Usuarios ({users.length})</div>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-yellow-400" /></div>
        ) : users.length === 0 ? (
          <div className="text-center py-10 text-white/40 text-sm">Aún no hay usuarios. Crea el primero arriba.</div>
        ) : (
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.username}`} className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-yellow-400/20 border border-yellow-400/40 flex items-center justify-center flex-shrink-0">
                  <KeyRound className="w-4 h-4 text-yellow-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-sm truncate">{u.display_name || u.username}</div>
                  <div className="text-[10px] text-white/50 flex items-center gap-2">
                    <span className="font-mono">{u.username}</span>
                    <span>·</span>
                    <span className={`uppercase tracking-widest px-1.5 py-0.5 rounded ${roleColor(u.role)}`}>{roleLabel(u.role)}</span>
                  </div>
                </div>
                <button onClick={() => del(u)} data-testid={`user-delete-${u.username}`} className="text-white/40 hover:text-red-500 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

